/**
 * Store-only ZIP (no compression) for Smoke Drive multi-file download.
 * Avoids a zip library dependency.
 */

function crc32Table() {
    const table = new Uint32Array(256);
    for (let i = 0; i < 256; i += 1) {
        let c = i;
        for (let k = 0; k < 8; k += 1) {
            c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
        }
        table[i] = c >>> 0;
    }
    return table;
}

const CRC_TABLE = crc32Table();

function utf8Bytes(text) {
    const value = String(text || '');
    if (typeof TextEncoder !== 'undefined') {
        return new TextEncoder().encode(value);
    }
    const out = [];
    for (let i = 0; i < value.length; i += 1) {
        const code = value.charCodeAt(i);
        if (code < 128) out.push(code);
        else out.push(95);
    }
    return Uint8Array.from(out);
}

export function crc32(bytes) {
    let crc = 0xffffffff;
    for (let i = 0; i < bytes.length; i += 1) {
        crc = CRC_TABLE[(crc ^ bytes[i]) & 0xff] ^ (crc >>> 8);
    }
    return (crc ^ 0xffffffff) >>> 0;
}

function u16(value) {
    const b = new Uint8Array(2);
    b[0] = value & 0xff;
    b[1] = (value >>> 8) & 0xff;
    return b;
}

function u32(value) {
    const b = new Uint8Array(4);
    b[0] = value & 0xff;
    b[1] = (value >>> 8) & 0xff;
    b[2] = (value >>> 16) & 0xff;
    b[3] = (value >>> 24) & 0xff;
    return b;
}

function concat(parts) {
    const total = parts.reduce((sum, p) => sum + p.length, 0);
    const out = new Uint8Array(total);
    let offset = 0;
    parts.forEach((part) => {
        out.set(part, offset);
        offset += part.length;
    });
    return out;
}

function uniqueZipName(name, used) {
    const raw = String(name || 'file').replace(/\\/g, '/').replace(/^\/+/, '') || 'file';
    if (!used.has(raw.toLowerCase())) {
        used.add(raw.toLowerCase());
        return raw;
    }
    const dot = raw.lastIndexOf('.');
    const base = dot > 0 ? raw.slice(0, dot) : raw;
    const ext = dot > 0 ? raw.slice(dot) : '';
    let i = 2;
    let next = `${base} (${i})${ext}`;
    while (used.has(next.toLowerCase())) {
        i += 1;
        next = `${base} (${i})${ext}`;
    }
    used.add(next.toLowerCase());
    return next;
}

/**
 * @param {{ name: string, bytes: Uint8Array }[]} entries
 * @returns {Blob}
 */
export function buildZipStoreArchive(entries) {
    const list = Array.isArray(entries) ? entries : [];
    const used = new Set();
    const locals = [];
    const centrals = [];
    let offset = 0;

    list.forEach((entry) => {
        const name = uniqueZipName(entry.name, used);
        const nameBytes = utf8Bytes(name);
        const data = entry.bytes instanceof Uint8Array ? entry.bytes : new Uint8Array(entry.bytes || []);
        const crc = crc32(data);
        const size = data.length;
        const local = concat([
            u32(0x04034b50),
            u16(20),
            u16(0x0800),
            u16(0),
            u16(0),
            u16(0),
            u32(crc),
            u32(size),
            u32(size),
            u16(nameBytes.length),
            u16(0),
            nameBytes,
            data,
        ]);
        const central = concat([
            u32(0x02014b50),
            u16(20),
            u16(20),
            u16(0x0800),
            u16(0),
            u16(0),
            u16(0),
            u32(crc),
            u32(size),
            u32(size),
            u16(nameBytes.length),
            u16(0),
            u16(0),
            u16(0),
            u16(0),
            u32(0),
            u32(offset),
            nameBytes,
        ]);
        locals.push(local);
        centrals.push(central);
        offset += local.length;
    });

    const centralDir = concat(centrals);
    const eocd = concat([
        u32(0x06054b50),
        u16(0),
        u16(0),
        u16(list.length),
        u16(list.length),
        u32(centralDir.length),
        u32(offset),
        u16(0),
    ]);

    return new Blob([concat([...locals, centralDir, eocd])], { type: 'application/zip' });
}
